import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../CustomHeadlessTable/CustomTable";
import CustomInputComponent from "../../../../CustomHeadlessTable/components/CustomInputComponent";
import {
  getAllTransportation38LCA,
  addTransportation38LCA,
  updateTransportation38LCA,
  deleteTransportation38LCA,
} from "../../../../../apis/Product/TransportationFlow38Api";
import {
  getProductTransportationMeans38Dropdown,
  getProductSurplusTransportCountryDropdown,
} from "../../../../../apis/Dropdown";
import { IconButton } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { TableCompareTrigger } from "../../components/lcaComparison";

// Impact categories configuration
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  { name: "Photochemical ozone formation", unit: "kg NMVOC-eq", key: "smog" },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  { name: "Eutrophication, terrestrial", unit: "mol N-eq", key: "eutrophication_terrestrial" },
  { name: "Eutrophication, freshwater", unit: "kg P-eq", key: "eutrophication_freshwater" },
  { name: "Eutrophication, marine", unit: "kg N-eq", key: "eutrophication_marine" },
  { name: "Human toxicity, cancer effects", unit: "CTUh", key: "carcinogenics" },
  { name: "Human toxicity, non-cancer effects", unit: "CTUh", key: "non_carcinogenics" },
  { name: "Particulate matter", unit: "disease incidence", key: "respiratory_effects" },
  { name: "Ecotoxicity, freshwater", unit: "CTUe", key: "ecotoxicity" },
  { name: "Resource use, energy carriers", unit: "MJ", key: "fossil_fuel_depletion" },
  { name: "Land use", unit: "Pt", key: "land_use" },
  { name: "Water use", unit: "m³ world-eq deprived", key: "water_use" },
  { name: "Resource use, mineral and metals", unit: "kg Sb-eq", key: "resource_use_minerals_metals" },
  { name: "Ionising radiation, human health", unit: "kBq U235-eq", key: "ionising_radiation" },
];

const Transportation38Table = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] = useState(0);
  const [transportMeansDropdown, setTransportMeansDropdown] = useState([]);
  const [countryDropdown, setCountryDropdown] = useState([]);
  const [unitTransportMeans, setUnitTransportMeans] = useState({});

  // Fetch dropdowns
  useEffect(() => {
    getProductTransportationMeans38Dropdown({
      setDropdown: setTransportMeansDropdown,
    });
    getProductSurplusTransportCountryDropdown({
      setDropdown: setCountryDropdown,
    });
  }, []);

  // Convert country codes to names when dropdowns are loaded
  useEffect(() => {
    if (data.length > 0 && countryDropdown.length > 0) {
      setData((prevData) => {
        let hasChanges = false;
        const convertedData = prevData.map((item) => {
          const updates = {};
          
          // Convert departure_country code to name
          if (item.departure_country && typeof item.departure_country === "string") {
            if (item.departure_country.length <= 3) {
              const foundCountry = countryDropdown.find(
                (option) => option.code === item.departure_country
              );
              if (foundCountry && item.departure_country !== foundCountry.name) {
                hasChanges = true;
                updates.departure_country = foundCountry.name;
              }
            }
          }
          
          // Convert destination_country code to name
          if (item.destination_country && typeof item.destination_country === "string") {
            if (item.destination_country.length <= 3) {
              const foundCountry = countryDropdown.find(
                (option) => option.code === item.destination_country
              );
              if (foundCountry && item.destination_country !== foundCountry.name) {
                hasChanges = true;
                updates.destination_country = foundCountry.name;
              }
            }
          }
          
          return hasChanges ? { ...item, ...updates } : item;
        });
        return hasChanges ? convertedData : prevData;
      });
    }
  }, [data, countryDropdown]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  // Update units when data or dropdown changes
  useEffect(() => {
    if (data.length > 0 && transportMeansDropdown.length > 0) {
      const newUnitTransportMeans = {};
      data.forEach((row) => {
        const transportMeansCode =
          typeof row.means_of_transportation === "string"
            ? row.means_of_transportation
            : row.means_of_transportation?.value || row.means_of_transportation?.code;
        if (transportMeansCode) {
          const selectedMeans = transportMeansDropdown.find(
            (option) =>
              !option.isCategory &&
              (option.code === transportMeansCode ||
                option.value === transportMeansCode)
          );
          if (selectedMeans) {
            newUnitTransportMeans[row.id] = selectedMeans.unit || "tkm";
          }
        }
      });
      setUnitTransportMeans(newUnitTransportMeans);
    }
  }, [data, transportMeansDropdown]);

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];

  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === 0 ? IMPACT_CATEGORIES.length - 1 : prev - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === IMPACT_CATEGORIES.length - 1 ? 0 : prev + 1
    );
  }, []);

  const fetchData = useCallback(() => {
    if (productId) {
      getAllTransportation38LCA({
        productId,
        setObjects: setData,
      });
    }
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = useCallback(
    (editedItem) => {
      const transportMeansCode =
        typeof editedItem.means_of_transportation === "string"
          ? editedItem.means_of_transportation
          : editedItem.means_of_transportation?.value || editedItem.means_of_transportation?.code || null;
      
      // Convert country names back to codes for backend
      let departureCountryCode = editedItem.departure_country;
      if (departureCountryCode) {
        // Handle object values (from dropdown selection)
        if (typeof departureCountryCode === "object") {
          departureCountryCode = departureCountryCode.code || departureCountryCode.value || departureCountryCode.name || null;
        }
        // Handle string values - convert name to code if it's a full name
        else if (typeof departureCountryCode === "string" && departureCountryCode.length > 3) {
          const foundCountry = countryDropdown.find(
            (option) => option.name === departureCountryCode || option.value === departureCountryCode
          );
          if (foundCountry) {
            departureCountryCode = foundCountry.code;
          }
        }
      }
      
      let destinationCountryCode = editedItem.destination_country;
      if (destinationCountryCode) {
        // Handle object values (from dropdown selection)
        if (typeof destinationCountryCode === "object") {
          destinationCountryCode = destinationCountryCode.code || destinationCountryCode.value || destinationCountryCode.name || null;
        }
        // Handle string values - convert name to code if it's a full name
        else if (typeof destinationCountryCode === "string" && destinationCountryCode.length > 3) {
          const foundCountry = countryDropdown.find(
            (option) => option.name === destinationCountryCode || option.value === destinationCountryCode
          );
          if (foundCountry) {
            destinationCountryCode = foundCountry.code;
          }
        }
      }
      
      // Packaging is a text field, keep as string
      const packagingValue = editedItem.packaging_included || null;
      
      updateTransportation38LCA({
        productId,
        rowId: editedItem.id,
        means_of_transportation: transportMeansCode,
        city_of_departure: editedItem.city_of_departure || null,
        departure_country: departureCountryCode || null,
        city_of_destination: editedItem.city_of_destination || null,
        destination_country: destinationCountryCode || null,
        transport_distance: editedItem.transport_distance || 0,
        fu_transported: editedItem.fu_transported || 0,
        packaging_included: packagingValue,
        manual_emission_factor: editedItem.manual_emission_factor || null,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [productId, data, countryDropdown]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const transportMeansCode =
        typeof newItem.means_of_transportation === "string"
          ? newItem.means_of_transportation
          : newItem.means_of_transportation?.value || newItem.means_of_transportation?.code || null;
      
      // Convert country names back to codes for backend
      let departureCountryCode = newItem.departure_country;
      if (departureCountryCode) {
        // Handle object values (from dropdown selection)
        if (typeof departureCountryCode === "object") {
          departureCountryCode = departureCountryCode.code || departureCountryCode.value || departureCountryCode.name || null;
        }
        // Handle string values - convert name to code if it's a full name
        else if (typeof departureCountryCode === "string" && departureCountryCode.length > 3) {
          const foundCountry = countryDropdown.find(
            (option) => option.name === departureCountryCode || option.value === departureCountryCode
          );
          if (foundCountry) {
            departureCountryCode = foundCountry.code;
          }
        }
      }
      
      let destinationCountryCode = newItem.destination_country;
      if (destinationCountryCode) {
        // Handle object values (from dropdown selection)
        if (typeof destinationCountryCode === "object") {
          destinationCountryCode = destinationCountryCode.code || destinationCountryCode.value || destinationCountryCode.name || null;
        }
        // Handle string values - convert name to code if it's a full name
        else if (typeof destinationCountryCode === "string" && destinationCountryCode.length > 3) {
          const foundCountry = countryDropdown.find(
            (option) => option.name === destinationCountryCode || option.value === destinationCountryCode
          );
          if (foundCountry) {
            destinationCountryCode = foundCountry.code;
          }
        }
      }
      
      // Packaging is a text field, keep as string
      const packagingValue = newItem.packaging_included || null;
      
      addTransportation38LCA({
        productId,
        means_of_transportation: transportMeansCode,
        city_of_departure: newItem.city_of_departure || null,
        departure_country: departureCountryCode || null,
        city_of_destination: newItem.city_of_destination || null,
        destination_country: destinationCountryCode || null,
        transport_distance: newItem.transport_distance || 0,
        fu_transported: newItem.fu_transported || 0,
        packaging_included: packagingValue,
        manual_emission_factor: newItem.manual_emission_factor || null,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData, countryDropdown]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteTransportation38LCA({
        productId,
        rowId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const impactCategoryAccessor = currentImpactCategory.key;

  const columns = useMemo(
    () => [
      {
        id: "means_of_transportation",
        accessor: "means_of_transportation",
        header: `${t("MEANS_OF_TRANSPORTATION") || "Means of transportation"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_TRANSPORT_MEANS") || "Select transport means...",
        unit: (rowId) => unitTransportMeans[rowId] || "xx",
      },
      {
        id: "city_of_departure",
        accessor: "city_of_departure",
        header: t("CITY_OF_DEPARTURE") || "City of departure",
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
        optional: true,
      },
      {
        id: "departure_country",
        accessor: "departure_country",
        header: `${t("CHOOSE_COUNTRY_DEPARTURE") || "Choose country of departure"} *`,
        type: "dropdown",
        placeholder: t("SELECT_COUNTRY") || "Select country...",
        optional: true,
      },
      {
        id: "city_of_destination",
        accessor: "city_of_destination",
        header: t("CITY_OF_ARRIVAL") || "City of arrival",
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
        optional: true,
      },
      {
        id: "destination_country",
        accessor: "destination_country",
        header: `${t("CHOOSE_COUNTRY_ARRIVAL") || "Choose country of arrival"} *`,
        type: "dropdown",
        placeholder: t("SELECT_COUNTRY") || "Select country...",
        optional: true,
      },
      {
        id: "transport_distance",
        accessor: "transport_distance",
        header: `${t("TRANSPORT_DISTANCE_KM") || "Transport distance"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: () => "km",
      },
      {
        id: "fu_transported",
        accessor: "fu_transported",
        header: `${t("FU_TRANSPORTED_38") || "How much of the FU is transported?"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: () => "kg",
      },
      {
        id: "packaging_included",
        accessor: "packaging_included",
        header:
          t("PACKAGING_INCLUDED_38") ||
          "How much packaging is included in the transport between A5 and B?",
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
        optional: true,
        unit: () => "kg",
      },
      {
        id: "manual_emission_factor",
        accessor: "manual_emission_factor",
        header: `${t("MANUAL_EMISSION_FACTOR_38") || "Manual: xx/tkm (e.g. for specific means of transportation)"}`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        optional: true,
      },
      {
        id: currentImpactCategory.key,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton size="small" onClick={handlePreviousImpactCategory} style={{ padding: "4px" }}>
              <KeyboardArrowLeft fontSize="small" />
            </IconButton>
            <span>
              {currentImpactCategory.name} [{currentImpactCategory.unit}]
            </span>
            <IconButton size="small" onClick={handleNextImpactCategory} style={{ padding: "4px" }}>
              <KeyboardArrowRight fontSize="small" />
            </IconButton>
            <TableCompareTrigger />
          </div>
        ),
        accessor: currentImpactCategory.key,
        type: "custom",
        optional: true,
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          const impactValue =
            row.original[currentCategory.key] ||
            row.original[`impact_${currentCategory.key}`] ||
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ],
    [
      t,
      currentImpactCategory,
      currentImpactCategoryIndex,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
      unitTransportMeans,
    ]
  );

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true,
    }),
    [impactCategoryAccessor]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: { label: `Total ${currentImpactCategory.name}` },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      means_of_transportation: transportMeansDropdown || [],
      departure_country: countryDropdown || [],
      destination_country: countryDropdown || [],
    }),
    [transportMeansDropdown, countryDropdown]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={useMemo(
        () => ({ [impactCategoryAccessor]: CustomInputComponent }),
        [impactCategoryAccessor]
      )}
      comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
    />
  );
};

export default Transportation38Table;

